import { useState, useEffect } from 'react'
import { ImageIcon } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/shared/components/ui/card'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useGetOrgId } from 'src/api/helpers/getOrgId'
import { useToast } from 'src/shared/hooks/use-toast'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { useHandleLogos } from 'src/shared/hooks/useHandleLogos'
import { cleanUrl } from 'src/shared/utils/helpers'
import { WebsiteUrlForm } from 'src/pages/_myAccount/EditDecoration/components/ArtworkPicker/components/WebsiteUrlForm'

export const LogosSection = () => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const org_id = useGetOrgId()
  const { organizationById, updateOrganization } = useOrgsApi({ org_id })
  const companyUrl = organizationById?.data?.company_url ?? null
  const { brandfetchUrls, refreshBrandfetch } = useHandleLogos({
    org_id,
    companyUrl,
    skipStorageList: true,
  })
  const [websiteUrlInput, setWebsiteUrlInput] = useState('')
  const [isSubmittingWebsiteUrl, setIsSubmittingWebsiteUrl] = useState(false)

  useEffect(() => {
    if (companyUrl) setWebsiteUrlInput(companyUrl)
  }, [companyUrl])

  const handleSubmitWebsiteUrl = async () => {
    if (!websiteUrlInput.trim() || !org_id) return

    try {
      setIsSubmittingWebsiteUrl(true)
      const cleanedUrl = cleanUrl(websiteUrlInput.trim())
      await updateOrganization.mutateAsync({
        id: org_id,
        company_url: cleanedUrl,
      })
      setWebsiteUrlInput(cleanedUrl)
      refreshBrandfetch()
    } catch (error) {
      console.error('Error updating website URL:', error)
      toast({
        variant: 'destructive',
        title: t(
          'Failed to update website URL. Please try again.',
          'Kunde inte uppdatera webbplats-URL. Försök igen.'
        ),
      })
    } finally {
      setIsSubmittingWebsiteUrl(false)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <Row className="items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
            <ImageIcon className="size-5 text-muted-foreground" />
          </div>
          <Column className="min-w-0 flex-1 gap-1">
            <CardTitle>{t('Logos', 'Logotyper')}</CardTitle>
            <CardDescription>
              {t(
                'We pull logos from your site; upload files for full control.',
                'Vi hämtar logotyper från din webbplats; ladda upp filer för full kontroll.'
              )}
            </CardDescription>
          </Column>
        </Row>
      </CardHeader>

      <CardContent>
        <Column className="gap-5">
          {brandfetchUrls.length > 0 ? (
            <Column className="gap-2">
              <span className="text-xs text-muted-foreground">
                {t('From your website', 'Från din webbplats')}
              </span>
              <Column className="rounded-lg border border-border/80 bg-muted/30 p-3 gap-3">
                <Row className="flex-wrap gap-2">
                  {brandfetchUrls.map((url, index) => (
                    <div
                      key={`brandfetch-${url}-${index}`}
                      className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-background p-1.5 shadow-sm"
                    >
                      <img
                        src={url}
                        alt=""
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  ))}
                </Row>

                <WebsiteUrlForm
                  websiteUrlInput={websiteUrlInput}
                  onWebsiteUrlInputChange={setWebsiteUrlInput}
                  isSubmitting={isSubmittingWebsiteUrl}
                  onSubmit={handleSubmitWebsiteUrl}
                  mode={companyUrl ? 'refresh' : 'new'}
                />
              </Column>
            </Column>
          ) : null}
        </Column>
      </CardContent>
    </Card>
  )
}

LogosSection.displayName = 'LogosSection'
